import { apiClient } from '../config/api'
import type {
  AudienceEstimate,
  DeliveryReport,
  EmailTemplate,
  Locale,
  MailBranding,
  MailDiagnostic,
  MailLayout,
  MailProvider,
  MailSettings,
  MailSuppression,
  NewsletterAudience,
  NewsletterCampaign,
  NewsletterContact,
  NewsletterDraft,
  NewsletterRecipient,
  NewsletterStats,
  NewsletterSubscription,
  TemplateCatalog,
  TemplatePreview
} from '../types/communications'

const MAIL_CODES = new Set([
  'MAIL_UNCONFIGURED',
  'MAIL_DNS_FAILED',
  'MAIL_CONNECTION_REFUSED',
  'MAIL_CONNECTION_TIMEOUT',
  'MAIL_TLS_MISMATCH',
  'MAIL_CERTIFICATE_INVALID',
  'MAIL_AUTH_FAILED',
  'MAIL_SENDER_REJECTED',
  'MAIL_RECIPIENT_REJECTED',
  'MAIL_RATE_LIMITED',
  'MAIL_TEMPORARY_FAILURE',
  'MAIL_VALIDATION_FAILED',
  'MAIL_UNKNOWN'
])

export const emailIsValid = (value: string) => /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(value.trim())

export const apiErrorPayload = (error: any): Record<string, any> => {
  const payload = error?.response?.data
  if (payload && typeof payload === 'object') return payload
  if (typeof payload === 'string') {
    try {
      const parsed = JSON.parse(payload)
      if (parsed && typeof parsed === 'object') return parsed
    } catch {
      // Provider stacks and unstructured transport errors are deliberately not surfaced.
    }
  }
  return {}
}

export const apiErrorCode = (error: any, fallback = 'REQUEST_FAILED') => {
  const payload = apiErrorPayload(error)
  return String(payload.code || payload.error || fallback)
}

export const apiErrorMessage = (error: any, fallback: string) => {
  const payload = apiErrorPayload(error)
  return String(payload.message || fallback)
}

export const mailDiagnosticFromError = (error: any): MailDiagnostic => {
  const payload = apiErrorPayload(error)
  const code = MAIL_CODES.has(String(payload.code || payload.error))
    ? String(payload.code || payload.error)
    : 'MAIL_UNKNOWN'
  return {
    code,
    title: String(payload.title || ''),
    message: String(payload.message || ''),
    field: typeof payload.field === 'string' ? payload.field : null,
    suggestions: Array.isArray(payload.suggestions) ? payload.suggestions.map(String) : []
  }
}

const normalizeMail = (row: any): MailSettings => ({
  host: String(row.host || ''),
  port: Number(row.port || 587),
  secure: row.secure === true,
  username: String(row.username || ''),
  password: '',
  passwordConfigured: row.passwordConfigured === true,
  maskedPassword: String(row.maskedPassword || ''),
  fromName: String(row.fromName || ''),
  fromAddress: String(row.fromAddress || ''),
  replyTo: String(row.replyTo || ''),
  configured: row.configured === true,
  status: String(row.status || ''),
  updatedAt: row.updatedAt
})

export const communicationsService = {
  async getMailSettings(): Promise<MailSettings> {
    return normalizeMail((await apiClient.get('/settings/mail')).data)
  },

  async saveMailSettings(settings: MailSettings): Promise<MailSettings> {
    const body: Record<string, unknown> = {
      host: settings.host.trim(),
      port: settings.port,
      secure: settings.secure,
      username: settings.username.trim(),
      fromName: settings.fromName.trim(),
      fromAddress: settings.fromAddress.trim(),
      replyTo: settings.replyTo.trim()
    }
    if (settings.password) body.password = settings.password
    const response = await apiClient.put('/settings/mail', body)
    return {
      ...settings,
      password: '',
      configured: response.data.configured === true,
      status: String(response.data.status || 'Configured'),
      passwordConfigured: response.data.passwordConfigured === true
    }
  },

  async testMail(to: string, locale: Locale): Promise<void> {
    await apiClient.post('/settings/mail/test', { to: to.trim(), locale })
  },

  async getMailProviders(locale: Locale): Promise<MailProvider[]> {
    return (await apiClient.get('/settings/mail/providers', { locale })).data.providers
  },

  async getBranding(): Promise<MailBranding> {
    return (await apiClient.get('/settings/mail/branding')).data
  },

  async saveBranding(branding: MailBranding): Promise<MailBranding> {
    return (await apiClient.put('/settings/mail/branding', {
      appName: branding.appName.trim(),
      logoUrl: branding.logoUrl.trim(),
      accentColor: branding.accentColor,
      theme: branding.theme,
      footerNote: branding.footerNote.trim(),
      postalAddress: branding.postalAddress.trim(),
      supportEmail: branding.supportEmail.trim()
    })).data
  },

  async listTemplates(): Promise<TemplateCatalog> {
    const { data } = await apiClient.get('/settings/email-templates')
    return {
      templates: Array.isArray(data.templates) ? data.templates : [],
      categories: Array.isArray(data.categories) ? data.categories : [],
      layouts: Array.isArray(data.layouts) ? data.layouts : [],
      variables: data.variables || {}
    }
  },

  async saveTemplate(template: EmailTemplate): Promise<EmailTemplate> {
    return (await apiClient.put(
      `/settings/email-templates/${encodeURIComponent(template.templateKey)}/${template.locale}`,
      {
        subjectTemplate: template.subjectTemplate,
        headingTemplate: template.headingTemplate,
        preheaderTemplate: template.preheaderTemplate,
        bodyBlocks: template.bodyBlocks,
        layoutKey: template.layoutKey,
        actionLabelTemplate: template.actionLabelTemplate,
        isEnabled: template.isEnabled
      }
    )).data
  },

  async resetTemplate(templateKey: string, locale: Locale): Promise<EmailTemplate> {
    return (await apiClient.post(
      `/settings/email-templates/${encodeURIComponent(templateKey)}/${locale}/reset`
    )).data
  },

  // The draft is sent along so the preview shows unsaved edits.
  async previewTemplate(
    template: EmailTemplate,
    variables?: Record<string, string>,
    branding?: Partial<MailBranding>
  ): Promise<TemplatePreview> {
    return (await apiClient.post(
      `/settings/email-templates/${encodeURIComponent(template.templateKey)}/${template.locale}/preview`,
      {
        draft: {
          subjectTemplate: template.subjectTemplate,
          headingTemplate: template.headingTemplate,
          preheaderTemplate: template.preheaderTemplate,
          bodyBlocks: template.bodyBlocks,
          layoutKey: template.layoutKey,
          actionLabelTemplate: template.actionLabelTemplate
        },
        variables,
        branding
      }
    )).data
  },

  async testTemplate(templateKey: string, locale: Locale, to: string, variables?: Record<string, string>): Promise<void> {
    await apiClient.post(
      `/settings/email-templates/${encodeURIComponent(templateKey)}/${locale}/test`,
      { to: to.trim(), variables }
    )
  },

  async listCampaigns(): Promise<NewsletterCampaign[]> {
    return (await apiClient.get('/newsletters')).data
  },

  async getCampaignDesigns(): Promise<MailLayout[]> {
    const { data } = await apiClient.get('/newsletters/designs')
    return Array.isArray(data.designs) ? data.designs : []
  },

  async getNewsletterStats(): Promise<NewsletterStats> {
    return (await apiClient.get('/newsletters/stats')).data
  },

  async createCampaign(draft: NewsletterDraft): Promise<NewsletterCampaign> {
    return (await apiClient.post('/newsletters', draft)).data
  },

  async updateCampaign(id: number, draft: NewsletterDraft): Promise<NewsletterCampaign> {
    return (await apiClient.put(`/newsletters/${id}`, draft)).data
  },

  async deleteCampaign(id: number): Promise<void> {
    await apiClient.delete(`/newsletters/${id}`)
  },

  async estimateAudience(audience: NewsletterAudience): Promise<AudienceEstimate> {
    return (await apiClient.post('/newsletters/audience/preview', { audience })).data
  },

  async previewCampaign(draft: NewsletterDraft, locale: Locale): Promise<TemplatePreview> {
    return (await apiClient.post('/newsletters/preview', { ...draft, locale })).data
  },

  async sendCampaignNow(id: number): Promise<{ campaign: NewsletterCampaign; recipientCount: number }> {
    return (await apiClient.post(`/newsletters/${id}/send`)).data
  },

  async retryCampaign(id: number): Promise<{ campaign: NewsletterCampaign; requeued: number }> {
    return (await apiClient.post(`/newsletters/${id}/retry`)).data
  },

  async listCampaignRecipients(id: number, status?: string): Promise<{
    total: number
    recipients: NewsletterRecipient[]
  }> {
    const { data } = await apiClient.get(
      `/newsletters/${id}/recipients`,
      status ? { status, limit: 200 } : { limit: 200 }
    )
    return { total: Number(data.total || 0), recipients: Array.isArray(data.recipients) ? data.recipients : [] }
  },

  async listSubscriptions(): Promise<NewsletterSubscription[]> {
    const { data } = await apiClient.get('/newsletters/subscriptions')
    return Array.isArray(data) ? data : []
  },

  async setSubscription(email: string, status: 'SUBSCRIBED' | 'UNSUBSCRIBED', locale: Locale): Promise<void> {
    await apiClient.put(`/newsletters/subscriptions/${encodeURIComponent(email)}`, { status, locale })
  },

  async listContacts(): Promise<NewsletterContact[]> {
    const { data } = await apiClient.get('/newsletters/contacts')
    return Array.isArray(data) ? data : []
  },

  async approveContact(sourceType: 'CUSTOMER' | 'STAFF', sourceId: number, email: string, locale: Locale): Promise<void> {
    await apiClient.put(`/newsletters/contacts/${sourceType}/${sourceId}/approval`, { email, locale })
  },

  async revokeContact(sourceType: 'CUSTOMER' | 'STAFF', sourceId: number, email: string): Promise<void> {
    await apiClient.delete(`/newsletters/contacts/${sourceType}/${sourceId}/approval?email=${encodeURIComponent(email)}`)
  },

  async listSuppressions(): Promise<MailSuppression[]> {
    const { data } = await apiClient.get('/newsletters/suppressions')
    return Array.isArray(data) ? data : []
  },

  async removeSuppression(email: string): Promise<void> {
    await apiClient.delete(`/newsletters/suppressions/${encodeURIComponent(email)}`)
  },

  async testCampaign(id: number, to: string, locale: Locale): Promise<void> {
    await apiClient.post(`/newsletters/${id}/test`, { to: to.trim(), locale })
  },

  async scheduleCampaign(id: number, scheduledAt: string): Promise<{ campaign: NewsletterCampaign; recipientCount: number }> {
    return (await apiClient.post(`/newsletters/${id}/schedule`, { scheduledAt })).data
  },

  async cancelCampaign(id: number): Promise<NewsletterCampaign> {
    return (await apiClient.post(`/newsletters/${id}/cancel`)).data
  },

  async cloneCampaign(id: number): Promise<NewsletterCampaign> {
    return (await apiClient.post(`/newsletters/${id}/clone`)).data
  },

  async archiveCampaign(id: number): Promise<NewsletterCampaign> {
    return (await apiClient.post(`/newsletters/${id}/archive`)).data
  },

  async getCampaignReport(id: number): Promise<DeliveryReport> {
    return (await apiClient.get(`/newsletters/${id}/report`)).data
  },

  async unsubscribe(token: string): Promise<void> {
    await apiClient.post('/public/newsletter/unsubscribe', { token })
  },

  async requestPasswordReset(identifier: string, locale: Locale): Promise<void> {
    await apiClient.post('/auth/password/forgot', { email: identifier.trim(), locale })
  },

  async resetPassword(token: string, password: string, locale: Locale): Promise<void> {
    await apiClient.post('/auth/password/reset', { token, password, locale })
  },

  async requestEmailVerification(locale: Locale): Promise<{ alreadyVerified?: boolean }> {
    return (await apiClient.post('/auth/email-verification/request', { locale })).data
  },

  async resendEmailChange(currentPassword: string, locale: Locale): Promise<{ pendingEmail?: string }> {
    return (await apiClient.post('/profile/email-change/resend', { currentPassword, locale })).data
  },

  async confirmEmail(token: string): Promise<void> {
    await apiClient.post('/auth/email-verification/confirm', { token })
  }
}
